// Shared breakdown cost calculator
// Single source of truth for pricing logic used by both frontend and backend

import type { CostBreakdown } from '../types/breakdown';
import type { SubItemCategory } from '../types/subItemCategory';
import { isQtyRateCategory, BUILT_IN_KEYS } from '../types/subItemCategory';
import type { SubItemTypeMetaMap } from '../types/subItemTypeMeta';

// Minimal interface the calculator needs — both frontend SubItem and backend
// BreakdownSubItem structurally satisfy this without casts.
export interface CalculableSubItem {
  type: string;
  hours?: number | null;
  rate?: number | null;
  rateType?: string | null;
  unitKey?: string | null;
  amount?: number | null;
  note?: string | null;
  markupRate?: number | null;
}

export interface Rates {
  taxRate: number;
  markupRate: number;
}

/** Get the value of a single sub-item */
export function getSubItemValue(item: CalculableSubItem, typeMeta: SubItemTypeMetaMap): number {
  if (isQtyRateCategory(item.type, typeMeta)) {
    const hours = Number(item.hours) || 0;
    const rate = Number(item.rate) || 0;
    return hours * rate;
  }
  // amount-mode: reads amount only; null amount → 0 (no fallback to hours*rate)
  return Number(item.amount) || 0;
}

/** Calculate labor cost from sub-items */
export function calculateLaborCost(subItems: CalculableSubItem[]): number {
  return subItems
    .filter((item) => item.type === 'labor')
    .reduce((total, item) => {
      const hours = Number(item.hours) || 0;
      const rate = Number(item.rate) || 0;
      return total + hours * rate;
    }, 0);
}

/** Calculate total for a specific type */
export function calculateTypeTotal(subItems: CalculableSubItem[], type: string, typeMeta: SubItemTypeMetaMap): number {
  return subItems
    .filter((item) => item.type === type)
    .reduce((total, item) => total + getSubItemValue(item, typeMeta), 0);
}

/** Calculate the taxable base — sum of all sub-items in taxApplicable categories */
export function calculateTaxableBase(
  subItems: CalculableSubItem[],
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): number {
  let base = 0;
  for (const item of subItems) {
    if (item.type === BUILT_IN_KEYS.PROFIT || item.type === BUILT_IN_KEYS.DISCOUNT) continue;
    const cat = categories.find((c) => c.key === item.type);
    // Carpet keys (no match in categories) default to taxApplicable: false
    if (!cat || !cat.taxApplicable) continue;
    base += getSubItemValue(item, typeMeta);
  }
  return base;
}

/** Calculate tax on the taxable base */
export function calculateTax(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): number {
  const taxableBase = calculateTaxableBase(subItems, typeMeta, categories);
  return taxableBase * rates.taxRate;
}

/** Calculate subtotal before markup (all categories except profit/discount + tax) */
export function calculateSubtotal(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): number {
  const tax = calculateTax(subItems, rates, typeMeta, categories);
  const knownKeys = new Set(categories.map((c) => c.key));

  let subtotal = tax;
  for (const cat of categories) {
    if (cat.key === BUILT_IN_KEYS.PROFIT || cat.key === BUILT_IN_KEYS.DISCOUNT) continue;
    subtotal += calculateTypeTotal(subItems, cat.key, typeMeta);
  }

  // Include sub-items whose type isn't in the categories list (e.g. carpet_* keys,
  // which live in carpet_pricing_catalog). getSubItemValue handles their pricing via typeMeta.
  for (const item of subItems) {
    if (knownKeys.has(item.type)) continue;
    if (item.type === BUILT_IN_KEYS.PROFIT || item.type === BUILT_IN_KEYS.DISCOUNT) continue;
    subtotal += getSubItemValue(item, typeMeta);
  }

  return subtotal;
}

export interface MarkupResult {
  total: number;
  jobMarkup: number;
  customMarkup: number;
}

/** Calculate markup using per-sub-item rates where available.
 *  Each item contributes (itemValue + itemTax) × effectiveRate to either
 *  jobMarkup or customMarkup. itemTax is itemValue × taxRate when the item's
 *  category is taxApplicable, else 0. Mirrors the per-row Excel formula:
 *    markup = (cost components + tax) × rate
 *
 *  `markupApplicable` decides the DEFAULT only. An explicit per-item
 *  `markupRate` is a deliberate instruction and always applies — the editor
 *  offers a markup badge on these rows whatever the flag says, so letting the
 *  flag win made that badge a silent no-op.
 */
export function calculateMarkup(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): MarkupResult {
  let jobMarkup = 0;
  let customMarkup = 0;

  for (const item of subItems) {
    if (item.type === BUILT_IN_KEYS.PROFIT || item.type === BUILT_IN_KEYS.DISCOUNT) continue;
    const cat = categories.find((c) => c.key === item.type);
    // Carpet catalog items (no match in sub_item_categories) default to
    // markupApplicable: false — their catalog prices are already customer-
    // facing final prices, so stacking job-level markup would double-dip.
    const isMarkupApplicable = cat ? cat.markupApplicable : false;
    const hasOverride = item.markupRate != null;
    if (!isMarkupApplicable && !hasOverride) continue;

    const itemValue = getSubItemValue(item, typeMeta);
    // Carpet keys (no match in categories) default to taxApplicable: false
    const isTaxable = cat ? cat.taxApplicable : false;
    const itemTax = isTaxable ? itemValue * rates.taxRate : 0;
    const taxableBase = itemValue + itemTax;

    if (hasOverride) {
      customMarkup += taxableBase * Number(item.markupRate);
    } else {
      jobMarkup += taxableBase * rates.markupRate;
    }
  }

  return { total: jobMarkup + customMarkup, jobMarkup, customMarkup };
}

/** Calculate total cost from sub-items */
export function calculateTotalCost(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): number {
  const subtotal = calculateSubtotal(subItems, rates, typeMeta, categories);
  const { total: markup } = calculateMarkup(subItems, rates, typeMeta, categories);
  const profit = calculateTypeTotal(subItems, BUILT_IN_KEYS.PROFIT, typeMeta);
  const discount = calculateTypeTotal(subItems, BUILT_IN_KEYS.DISCOUNT, typeMeta);

  return subtotal + markup + profit - discount;
}

/** Get full breakdown of all calculated values */
export function getCostBreakdown(
  subItems: CalculableSubItem[],
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
  roundingPrecision?: number | null,
): CostBreakdown {
  const tax = calculateTax(subItems, rates, typeMeta, categories);

  // Build category totals dynamically
  const categoryTotals: Record<string, number> = {};
  for (const cat of categories) {
    categoryTotals[cat.key] = calculateTypeTotal(subItems, cat.key, typeMeta);
  }

  // Include totals for any sub-item types not in the categories list (orphaned data)
  for (const item of subItems) {
    if (!(item.type in categoryTotals)) {
      categoryTotals[item.type] = (categoryTotals[item.type] || 0) + getSubItemValue(item, typeMeta);
    }
  }

  const profit = categoryTotals[BUILT_IN_KEYS.PROFIT] || 0;
  const discount = categoryTotals[BUILT_IN_KEYS.DISCOUNT] || 0;

  // Sum every non-profit / non-discount entry in categoryTotals — this
  // includes orphan keys (e.g. carpet_* sub-items that live in
  // carpet_pricing_catalog, not sub_item_categories). Iterating `categories`
  // alone would silently drop those contributions from the grand total.
  let subtotal = tax;
  for (const [key, value] of Object.entries(categoryTotals)) {
    if (key === BUILT_IN_KEYS.PROFIT || key === BUILT_IN_KEYS.DISCOUNT) continue;
    subtotal += value || 0;
  }

  const markupResult = calculateMarkup(subItems, rates, typeMeta, categories);
  const total = subtotal + markupResult.total + profit - discount;

  let roundedTotal = total;
  let rounding = 0;
  if (roundingPrecision && roundingPrecision > 0) {
    roundedTotal = Math.round(total / roundingPrecision) * roundingPrecision;
    rounding = roundedTotal - total;
    if (Math.abs(rounding) < 0.005) {
      rounding = 0;
      roundedTotal = total;
    }
  }

  return {
    categoryTotals,
    tax,
    subtotal,
    markup: markupResult.total,
    jobMarkup: markupResult.jobMarkup,
    customMarkup: markupResult.customMarkup,
    profit,
    discount,
    total,
    rounding,
    roundedTotal,
  };
}

/** Calculate the total cost for an array of line items with their sub-items */
export function calculateBreakdownTotal(
  lineItems: Array<{ subItems: CalculableSubItem[] }>,
  rates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): number {
  return lineItems.reduce((total, lineItem) => {
    return total + calculateTotalCost(lineItem.subItems, rates, typeMeta, categories);
  }, 0);
}

